import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api } from '@/api/client'
import type { User } from '@/api/types'

export type UiMode = 'standard' | 'classic'

const UI_MODE_CACHE_KEY = 'ledger:ui-mode'
const MOBILE_BREAKPOINT = 768

interface UiModeState {
  uiMode: UiMode
  classicModeExpired: boolean
  classicModeDaysRemaining: number | null
  classicModeExpiryDate: string | null
  graduatedTabs: string[]
  shouldShowNudge: boolean
  showExpiryBanner: boolean
  dismissExpiryBanner: () => void
  setUiMode: (mode: UiMode) => Promise<void>
  acceptGuidedTransition: () => Promise<void>
  declineGuidedTransition: () => void
}

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
}

export function useUiMode(user: User | null, onUserUpdate?: (user: User) => void): UiModeState {
  const [mode, setMode] = useState<UiMode>(() => {
    if (isMobile()) return 'standard'
    const cached = localStorage.getItem(UI_MODE_CACHE_KEY) as UiMode | null
    return cached || 'standard'
  })

  // Sync from server user on every user change
  useEffect(() => {
    if (!user) return
    if (isMobile()) {
      setMode('standard')
      return
    }
    const serverMode = user.effective_ui_mode || 'standard'
    setMode(serverMode)
    localStorage.setItem(UI_MODE_CACHE_KEY, serverMode)
  }, [user])

  // Listen for resize to force standard on mobile
  useEffect(() => {
    const handler = () => {
      if (isMobile()) setMode('standard')
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const classicModeExpired = user?.classic_mode_expired ?? false
  const classicModeDaysRemaining = user?.classic_mode_days_remaining ?? null
  const classicModeExpiryDate = user?.classic_mode_expiry_date ?? null
  const graduatedTabs = useMemo(() => user?.graduated_tabs ?? [], [user?.graduated_tabs])
  const shouldShowNudge = user?.should_show_nudge ?? false

  // Log milestone when graduated_tabs length increases
  const lastMilestoneRef = useRef(Number(user?.highest_graduated_milestone) || 0)
  useEffect(() => {
    const current = graduatedTabs.length
    if (current > 0 && current > lastMilestoneRef.current) {
      lastMilestoneRef.current = current
      const latestTab = graduatedTabs[graduatedTabs.length - 1]
      api.logTransitionEvent('classic_mode_tab_graduated', {
        tab: latestTab,
        milestone: current,
      }).catch(() => {})
    }
  }, [graduatedTabs])

  const setUiMode = useCallback(async (newMode: UiMode) => {
    if (isMobile()) return
    try {
      const updated = await api.updatePreferences({ ui_mode: newMode })
      setMode(newMode)
      localStorage.setItem(UI_MODE_CACHE_KEY, newMode)
      onUserUpdate?.(updated)
    } catch {
      // Revert to current
    }
  }, [onUserUpdate])

  const acceptGuidedTransition = useCallback(async () => {
    try {
      const updated = await api.updatePreferences({
        guided_transition_accepted_at: new Date().toISOString(),
      })
      onUserUpdate?.(updated)
    } catch {
      // Non-critical
    }
  }, [onUserUpdate])

  const declineGuidedTransition = useCallback(async () => {
    try {
      const updated = await api.updatePreferences({
        last_transition_nudged_at: new Date().toISOString(),
        guided_transition_declined_count: (user?.guided_transition_declined_count ?? 0) + 1,
      })
      onUserUpdate?.(updated)
    } catch {
      // Non-critical
    }
  }, [user, onUserUpdate])

  // Force standard if expired
  const [showExpiryBanner, setShowExpiryBanner] = useState(false)
  const expiryCleanedRef = useRef(false)

  useEffect(() => {
    if (classicModeExpired && mode === 'classic' && !expiryCleanedRef.current) {
      expiryCleanedRef.current = true
      setShowExpiryBanner(true)
      setMode('standard')
      localStorage.setItem(UI_MODE_CACHE_KEY, 'standard')
      // One-time cleanup: force server to standard
      api.updatePreferences({ ui_mode: 'standard' }).then(updated => {
        onUserUpdate?.(updated)
      }).catch(() => {})
      // Log the expiry event
      api.logTransitionEvent('classic_mode_expired').catch(() => {})
    }
  }, [classicModeExpired, mode, onUserUpdate])

  const dismissExpiryBanner = useCallback(() => {
    setShowExpiryBanner(false)
  }, [])

  const effectiveMode: UiMode = useMemo(() => {
    if (isMobile()) return 'standard' as UiMode
    if (classicModeExpired && mode === 'classic') return 'standard' as UiMode
    return mode
  }, [mode, classicModeExpired])

  return {
    uiMode: effectiveMode,
    classicModeExpired,
    classicModeDaysRemaining,
    classicModeExpiryDate,
    graduatedTabs,
    shouldShowNudge,
    showExpiryBanner,
    dismissExpiryBanner,
    setUiMode,
    acceptGuidedTransition,
    declineGuidedTransition,
  }
}
